import { transform } from 'lodash';

import type { Color } from '@didaskill/types/web';

import { getTextContrastColor } from '../../config/colors';
import type { COLORS, HUES } from '../../themes/default/colors';

/**
 * Creates a dark theme color palette by inverting colors from the original palette.
 *
 * For each hue, colors are inverted (mirrored): the lightest color becomes the darkest and vice versa.
 * This creates a consistent dark theme palette where all color relationships are preserved
 * but with inverted lightness values.
 *
 * @param originalHues - The original hue palette (HUES format) containing light colors.
 * @returns A new dark theme hue palette with the same structure as HUES, where each color
 *          is mapped to its mirrored counterpart (e.g., shade 15 becomes shade 900, etc.).
 *
 * @example
 * ```typescript
 * const darkHues = createDarkHues(HUES);
 * // darkHues.BLUE[900] will contain the color that was in HUES.BLUE[15]
 * // darkHues.BLUE[800] will contain the color that was in HUES.BLUE[25]
 * ```
 */
export const createDarkHues = (originalHues: typeof HUES) => {
  return transform(
    originalHues,
    (result, shades, hueKey) => {
      const keys = Object.keys(shades)
        .map(Number)
        .sort((a, b) => a - b);

      result[hueKey] = {};

      keys.forEach((key, index) => {
        const mirrorKey = keys[keys.length - 1 - index];

        result[hueKey][key] = shades[mirrorKey];
      });
    },
    {} as typeof HUES,
  );
};

/**
 * Creates a contrast color palette optimized for text readability.
 *
 * For each background color in the palette (hues and colors), this function calculates
 * the most readable text color using tinycolor's contrast algorithm.
 * The function iterates through all hues and colors, and for each color shade,
 * automatically determines the best text color (typically light or dark gray)
 * to ensure optimal readability.
 *
 * @param hues - The hue palette (HUES) to process.
 * @param colors - The color palette (COLORS) to process.
 * @returns An object containing contrast colors for each color group,
 *          with the same structure as the input hues and colors.
 *
 * @example
 * ```typescript
 * const contrastColors = createContrastColors(HUES, COLORS);
 * // contrastColors.primary[500] will contain the optimal text color for a primary[500] background
 * ```
 */
export const createContrastColors = (hues: typeof HUES, colors: typeof COLORS) => {
  return transform(
    { ...hues, ...colors },
    (acc: Record<string, Color>, colorGroupColors: Color, colorGroupName: string) => {
      acc[colorGroupName] = transform(
        colorGroupColors,
        (colorGroupAcc, colorCode, colorStrength) => {
          colorGroupAcc[colorStrength] = getTextContrastColor(colorCode, colors);
        },
      );
    },
  );
};
